import { getAbsoluteUrl, getBasePath } from '@repo/routing';
import deepmerge from 'deepmerge';
import { notFound } from 'next/navigation';
import { type NextRequest } from 'next/server';
import { type AbstractIntlMessages } from 'next-intl';
import { DEFAULT_LOCALE_CODE, LOCALE_CODES, LOCALE_COOKIE_NAME, type SupportedLanguage } from './constants';

interface GetI18nMessagesOptions {
  locale: SupportedLanguage;
  loader: (locale: string) => Promise<AbstractIntlMessages>;
  defaultLocale?: string;
}

export const getI18nMessages = async ({
  locale,
  loader,
  defaultLocale = DEFAULT_LOCALE_CODE,
}: GetI18nMessagesOptions): Promise<AbstractIntlMessages> => {
  const isAvailableLocale = LOCALE_CODES.includes(locale);

  if (!isAvailableLocale) notFound();

  const messages: AbstractIntlMessages = await loader(locale);

  if (locale !== defaultLocale) {
    const defaultMessages: AbstractIntlMessages = await loader(defaultLocale);

    return deepmerge(defaultMessages, messages);
  }

  return messages;
};

export const getRequestLocale = (request: NextRequest): string => {
  return request.cookies.get(LOCALE_COOKIE_NAME)?.value || DEFAULT_LOCALE_CODE;
};

export const getLocalizedBasePath = (path: string, request: NextRequest): string => {
  const locale = getRequestLocale(request);
  const basePath = getBasePath(path);

  if (locale === DEFAULT_LOCALE_CODE) {
    return basePath;
  }

  return `/${locale}${basePath}`;
};

export const getLocalizedAbsoluteUrl = (path: string, request: NextRequest, baseUrl?: string): string => {
  const locale = getRequestLocale(request);

  if (locale === DEFAULT_LOCALE_CODE) {
    return getAbsoluteUrl(path, baseUrl);
  }

  return getAbsoluteUrl(`/${locale}${path}`, baseUrl);
};

// todo: refactor to use reac-text component https://next-intl.dev/docs/usage/messages#rich-text
export const getDefaultTranslationValues = () => ({
  bold: (text: React.ReactNode) => <strong style={{ fontWeight: 700 }}>{text}</strong>,
  demibold: (text: React.ReactNode) => <strong style={{ fontWeight: 600 }}>{text}</strong>,
  br: () => <br />,
});

export const getPathnameWithoutLocale = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);

  const firstSegment = segments[0]?.toLowerCase();

  if (LOCALE_CODES.includes(firstSegment as SupportedLanguage)) {
    segments.splice(0, 1);
  }

  return `/${segments.join('/')}`;
};
